import { useState } from 'react'
import { Box, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { colors } from '../../const/colors'
import { IEmployeeUsageRow } from '../../graphql/parkingPool/statParkingPoolEmployeeUsage'

const COLLAPSED_ROWS = 5

interface IUsageRow extends IEmployeeUsageRow {
  name: string
  unusedDays: number
  usedPct: number | null
}

const toRow = (row: IEmployeeUsageRow): IUsageRow => ({
  ...row,
  name: [row.firstName, row.lastName].filter(Boolean).join(' ') || row.login,
  unusedDays: Math.max(0, row.reservedDays - row.usedDays),
  usedPct:
    row.reservedDays > 0
      ? Math.round((row.usedDays / row.reservedDays) * 100)
      : null,
})

const byUnusedDays = (a: IUsageRow, b: IUsageRow) =>
  b.unusedDays - a.unusedDays ||
  b.reservedDays - a.reservedDays ||
  a.name.localeCompare(b.name)

const CELL = { fontSize: '13px', fontWeight: 700, textAlign: 'right' as const }
const HEAD = {
  fontSize: '11px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: colors.slate,
  fontWeight: 700,
  textAlign: 'right' as const,
}

const EmployeeUsageSection = ({
  stats,
  pending,
  failed,
}: {
  stats: IEmployeeUsageRow[]
  pending: boolean
  failed: boolean
}) => {
  const { t } = useTranslation(['parking_pool_data', 'generic'])
  const [expanded, setExpanded] = useState(false)

  const rows = stats.map(toRow).sort(byUnusedDays)
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROWS)

  return (
    <Box
      sx={{
        background: colors.surface,
        border: `1px solid ${colors.border2}`,
        borderRadius: colors.radiusCard,
        boxShadow: colors.shadowCard,
        p: '20px',
        mb: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          mb: '14px',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ fontSize: '15px', fontWeight: 800 }}>
          {t('employee_usage_title')}
        </Box>
        <Box sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}>
          {t('employee_usage_hint')}
        </Box>
      </Box>

      {pending ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {[0, 1, 2].map(row => (
            <Box
              key={row}
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '1.6fr repeat(4, minmax(72px, 0.55fr)) 84px',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Skeleton variant="rounded" width="70%" height={18} />
              {[0, 1, 2, 3].map(cell => (
                <Skeleton
                  key={cell}
                  variant="rounded"
                  width={32}
                  height={18}
                  sx={{ ml: 'auto' }}
                />
              ))}
              <Skeleton variant="rounded" height={10} />
            </Box>
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Box
          title={failed ? t('employee_usage_error') : undefined}
          sx={{ fontSize: '13px', fontWeight: 700, color: colors.textMuted }}
        >
          {failed ? t('employee_usage_no_data') : t('employee_usage_empty')}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.6fr repeat(4, minmax(72px, 0.55fr)) 84px',
              alignItems: 'center',
              gap: '10px',
              pb: '8px',
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ ...HEAD, textAlign: 'left' }}>{t('employee_col_name')}</Box>
            <Box sx={HEAD}>{t('employee_col_reserved')}</Box>
            <Box sx={HEAD}>{t('employee_col_used')}</Box>
            <Box sx={HEAD}>{t('employee_col_unused')}</Box>
            <Box sx={HEAD}>{t('employee_col_cancelled')}</Box>
            <Box sx={HEAD}>{t('employee_col_ratio')}</Box>
          </Box>

          {visibleRows.map(row => (
            <Box
              key={row.login}
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '1.6fr repeat(4, minmax(72px, 0.55fr)) 84px',
                alignItems: 'center',
                gap: '10px',
                p: '10px 0',
                borderBottom: `1px solid ${colors.border2}`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color: colors.navy,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.name}
                </Box>
                <Box
                  sx={{
                    fontSize: '11.5px',
                    color: colors.textMuted,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.login}
                </Box>
              </Box>
              <Box sx={CELL}>{row.reservedDays}</Box>
              <Box sx={CELL}>{row.usedDays}</Box>
              <Box
                sx={{
                  ...CELL,
                  color: row.unusedDays > 0 ? '#8a6a00' : colors.textMuted,
                }}
              >
                {row.unusedDays}
              </Box>
              <Box
                sx={{
                  ...CELL,
                  color: row.cancelledDays > 0 ? colors.red : colors.textMuted,
                }}
              >
                {row.cancelledDays}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 5,
                    borderRadius: '3px',
                    background: colors.border,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(row.usedPct ?? 0, 100)}%`,
                      height: '100%',
                      background:
                        row.usedPct != null && row.usedPct < 50
                          ? colors.gold
                          : colors.green,
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: '11.5px',
                    fontWeight: 800,
                    color: colors.textSecondary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.usedPct != null ? `${row.usedPct}%` : '—'}
                </Box>
              </Box>
            </Box>
          ))}

          {rows.length > COLLAPSED_ROWS && (
            <Box
              onClick={() => setExpanded(open => !open)}
              sx={{
                mt: '12px',
                fontSize: '13px',
                fontWeight: 700,
                color: colors.navy,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {expanded
                ? t('employee_usage_collapse')
                : t('employee_usage_expand', { n: rows.length })}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default EmployeeUsageSection
